import {Component, inject} from '@angular/core';
import {FindPartnersForm} from './find-partners.model';
import firebase from 'firebase/compat/app';
import {Router, RouterModule, ActivatedRoute} from '@angular/router';
import {Partner} from '../interfaces/partner';
import {Teacher} from "../interfaces/teacher";
import {TeacherService} from "../services/teacher.service";
import Timestamp = firebase.firestore.Timestamp;
import {PartnerService} from "../services/partner.service";

@Component({
  selector: 'app-find-partners',
  templateUrl: './find-partners.component.html',
  styleUrls: [
    './find-partners.component.css',
    '../app.component.css'
  ],
})
export class FindPartnersComponent {
  findPartnersForm: FindPartnersForm = new FindPartnersForm();
  partners: Partner[] = [];
  searchText = "";
  servicesDropdownText = "Select Services";
  focusAreasDropdownText = "Select Focus Areas";
  teacher: Teacher | null = null;
  filteredPartners: Partner[] = [];
  hoveredRow: number | null = null;

  constructor(private router: Router, private teacherService: TeacherService, private partnerService: PartnerService) {
  };

  route: ActivatedRoute = inject(ActivatedRoute)

  // flag to keep track wich partners array display first example we want to display to the user all partners NOT filtered
  // then when it filters we need to uptade with the filtered array so we need this flag to do that
  formSubmitted: boolean = false;
  //then a new array comes which we use to update the partners array or the filterpartners array
  displayPartners: Partner[] = [];

  onPartnerCardClicked(urlOrg: any) {
    this.router.navigate(['/partner', urlOrg]);
  }

  filterResults() {
    const servicesOffered = this.findPartnersForm.servicesOffered;
    const focusAreas = this.findPartnersForm.focusAreas;
    const searchText = this.searchText.toLowerCase();

    this.filteredPartners = this.partners.filter(partner => {
      const matchesService = servicesOffered.length === 0 || servicesOffered.some(service => partner.servicesOffered.includes(this.findPartnersForm.filterLabels[service]));
      const matchesFocusArea = focusAreas.length === 0 || focusAreas.some(area => partner.focusAreas.includes(this.findPartnersForm.filterLabels[area]));
      const matchesText = partner.org.toLowerCase().includes(searchText) ||
        partner.primeContact.toLowerCase().includes(searchText);
      return matchesService && matchesFocusArea && matchesText;
    });

    this.currentPage = 1;
    this.updatePagination();
  }

  onServiceChanged(event: Event, service: string) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.findPartnersForm.servicesOffered.push(service);
    } else {
      this.findPartnersForm.servicesOffered = this.findPartnersForm.servicesOffered.filter(
        selected => selected !== service
      );
    }

    const selectedLength = this.findPartnersForm.servicesOffered.length;
    if (selectedLength > 0) {
      this.servicesDropdownText = selectedLength + " Selected";
    } else {
      this.servicesDropdownText = "Select Services"
    }

    this.filterResults();
  }

  onFocusAreaChanged(event: Event, area: string) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.findPartnersForm.focusAreas.push(area);
    } else {
      this.findPartnersForm.focusAreas = this.findPartnersForm.focusAreas.filter(
        selected => selected !== area
      );
    }

    const selectedLength = this.findPartnersForm.focusAreas.length;
    if (selectedLength > 0) {
      this.servicesDropdownText = selectedLength + " Selected";
    } else {
      this.servicesDropdownText = "Select Focus Areas"
    }

    this.filterResults();
  }

  async ngOnInit() {
    this.teacher = await this.teacherService.getTeacher();
    await this.getPartners();
    this.filteredPartners = [...this.partners];
    this.displayPartners = [...this.partners];

    // Default alphabetical order
    this.sorts = [{ field: 'partnerName', dir: 'asc' }];
    this.applySorting();
  }

  // all partners form the DB are pull and store to the parrner array.
  async getPartners() {
    const role = this.teacher?.role.toLowerCase();
    //const school = this.teacher?.school.toLowerCase();
    const snapshot = await firebase.firestore().collection('partner').get();
    snapshot.forEach((doc) => {
      const data = doc.data() as Partner;
      if (role !== 'admin' && data.isDeleted) return;
      if (Array.isArray(data['servicesOffered']) && data['servicesOffered'].length === 1 && data['servicesOffered'][0] === "") {
        data['servicesOffered'] = [];
      }
      this.partners.push(data as Partner);
    });
    this.updatePagination();
  }

  sortProjects() {
    const selectBox = document.getElementById("sortby") as HTMLSelectElement;
    const selectedValue = selectBox.value;

    if (selectedValue === "orgASC") {
      this.displayPartners.sort((a, b) => a.org.localeCompare(b.org));
    } else if (selectedValue === "orgDSC") {
      this.displayPartners.sort((a, b) => b.org.localeCompare(a.org));
    }
  }

  // PAGINATION
  cardsPerPage = 10;
  currentPage = 1;
  totalPages = 0;
  pages: number[] = [];

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredPartners.length / this.cardsPerPage);
    this.pages = Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  get pagedCards() {
    const start = (this.currentPage - 1) * this.cardsPerPage;
    return this.filteredPartners.slice(start, start + this.cardsPerPage);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPrevious() {
    if (this.currentPage > 1) this.currentPage--;
  }

  goToNext() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  selectChange(event: Event) {
    const numberSelected = event.target as HTMLSelectElement;
    if (numberSelected.value !== "All") {
      this.cardsPerPage = Number(numberSelected.value);
    } else {
      this.cardsPerPage = this.displayPartners.length;
    }
    this.updatePagination();
  }

  partnerExportColumns = [
    {header: 'Organization', field: 'org'},
    {header: 'Prime Contact', field: 'primeContact'},
    {header: 'Services Offered', field: 'servicesOffered'},
    {header: 'Focus Areas', field: 'focusAreas'},
    {header: 'Website', field: 'website'}
  ];
  downloadedTable = false;

  exportCSV(data: Partner[], fileName: string) {
    if (!data?.length) return;
    this.downloadedTable = false;

    const rows: string[] = [];

    // headers
    rows.push(this.partnerExportColumns.map(c => `"${c.header}"`).join(','));

    // rows
    data.forEach(partner => {
      rows.push(
        this.partnerExportColumns
          .map(c => {
            let val = (partner as any)[c.field] ?? '';
            if (Array.isArray(val)) val = val.join(', ');
            return `"${String(val).replace(/"/g, '""')}"`
          })
          .join(',')
      );
    });

    const blob = new Blob([rows.join('\r\n')], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
    this.downloadedTable = true;
  }

  sorts: { field: string; dir: 'asc' | 'desc' }[] = [];
  partnerStatusOrder = ['Active', 'Inactive'];

  sort(field: string) {
    const existing = this.sorts[0];

    if (existing?.field === field) {
      existing.dir = existing.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sorts = [{field, dir: 'asc'}];
    }

    this.applySorting();
  }

  isEmpty(val: any): boolean {
    return val === null ||
      val === undefined ||
      val === '' ||
      !String(val).trim();
  }

  selectedStatusSort: string | null = null;

  onStatusSortChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedStatusSort = select.value || null;

    this.sorts = [{field: 'partnerStatus', dir: 'asc'}];
    this.applySorting();
  }

  applySorting() {
    this.filteredPartners.sort((a, b) => {
      for (const s of this.sorts) {
        let aVal: any;
        let bVal: any;
        let result = 0;

        switch (s.field) {
          case 'partnerName':
            aVal = a.org?.trim().toLowerCase();
            bVal = b.org?.trim().toLowerCase();
            break;

          case 'contactName':
            aVal = a.primeContact?.trim().toLowerCase();
            bVal = b.primeContact?.trim().toLowerCase();
            break;

          case 'date':
            aVal = new Date(a.createdAt instanceof Timestamp ? a.createdAt.toDate() : a.createdAt ?? 0).getTime();
            bVal = new Date(b.createdAt instanceof Timestamp ? b.createdAt.toDate() : b.createdAt ?? 0).getTime();
            break;

          case 'updatedDate':
            aVal = new Date(a.modifiedAt instanceof Timestamp ? a.modifiedAt.toDate() : a.modifiedAt ?? 0).getTime();
            bVal = new Date(b.modifiedAt instanceof Timestamp ? b.modifiedAt.toDate() : b.modifiedAt ?? 0).getTime();
            break;

          case 'partnerStatus':
            const selected = this.selectedStatusSort;

            aVal = a.status;
            bVal = b.status;

            if (selected) {
              if (aVal === selected && bVal !== selected) return -1;
              if (aVal !== selected && bVal === selected) return 1;
            }
            result =
              this.rank(aVal, this.partnerStatusOrder) -
              this.rank(bVal, this.partnerStatusOrder);
            break;
        }

        const aEmpty = this.isEmpty(aVal);
        const bEmpty = this.isEmpty(bVal);

        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) continue;

        if (typeof aVal === 'string') {
          result = aVal.localeCompare(bVal);
        } else {
          result = aVal - bVal;
        }

        if (result !== 0) {
          return s.dir === 'asc' ? result : -result;
        }
      }
      return 0;
    });

    this.updatePagination();
  }

  rank(value: string, order: string[]): number {
    const i = order.indexOf(value);
    return i === -1 ? order.length : i;
  }

  getSortDir(field: string): 'asc' | 'desc' | null {
    const s = this.sorts.find(sort => sort.field === field);
    return s ? s.dir : null;
  }

  showPartners(event: Event) {
    const selection = event.target as HTMLSelectElement;
    const value = selection.value;

    if (value === 'All Partners') {
      this.filteredPartners = [...this.partners];
    } else if (value === 'Active') {
      this.filteredPartners = this.partners.filter(p => !p.isDeleted);
    } else if (value === 'Deleted') {
      this.filteredPartners = this.partners.filter(p => p.isDeleted);
    }

    this.currentPage = 1;
    this.updatePagination();
  }
}


